/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { DataQueue } from '../models/DataQueue.ts';
import { EnergyData } from '../models/EnergyData.ts';
import { SpeciesPopulationData } from '../models/speciesTally.ts';
import { DATA_QUEUE_LENGTH } from '../models/physicalConstants.ts';
import { PositionData } from '../models/PositionData.ts';
import { DipoleCurrentBuffer } from '../models/DipoleCurrentBuffer.ts';
import { VelocityBuffer } from '../models/VelocityBuffer.ts';
import { PositionBuffer } from '../models/PositionBuffer.ts';
import { MeasurementRow } from '../models/measurement.ts';
import { SpeedHistogramFrame } from '../models/speedHistogram.ts';

// One sample of the piston barostat's state, collected per collectInterval while
// the piston is on. Drives the "Boiling" analysis tab: plate height / volume /
// density vs temperature, from which the boiling point is read off.
// One sample of the lab's thermodynamic state: recorded in every lab (not
// only under the piston), so a time-trace card of the volume, density or
// pressure can be cut from it afterwards (agent/trajectorySegment.ts).
export interface PistonRow {
  time: number; // fs
  temperature: number; // K
  height: number; // Å (gas column height under the plate)
  volume: number; // Å³
  density: number; // kg/m³
  pressureMPa?: number; // MPa (absent on rows written before the field existed)
}

// Picked-measurement trace cap. Each row is one synchronized sample across all
// tracks (time + a small id→value map), so 5 000 rows is a few kB even with a
// handful of tracks and gives a long trace without the ~100-sample energy-queue
// limitation. Sampled at collectInterval like the energy series.
export const MEASUREMENT_BUFFER_CAPACITY = 5000;

// 25 000 samples ≈ 50 ps at a 2-fs sample stride, or 12.5 ps at 0.5 fs.
// Memory cost ≈ 400 KB (four Float32 components per sample). Plenty of
// headroom to resolve IR lines down to ~5 cm⁻¹ even with the shorter
// window. See src/models/irSpectrum.ts for the resolution arithmetic.
export const DIPOLE_BUFFER_CAPACITY = 25000;

// VDOS buffer caps. Tracking N atoms × 25 000 samples × 3 components ×
// 4 bytes — at the default cap of 16 atoms that's 4.8 MB plus a 100 KB
// time-stamp array. See VelocityBuffer for the element-balanced
// representative-atom pick that fills the slots.
export const VELOCITY_BUFFER_CAPACITY = 25000;
export const VELOCITY_BUFFER_MAX_ATOMS = 16;

// MSD buffer caps. Same shape and budget as VDOS — per-atom 3-vector
// time series for a representative subset of atoms. Distinct buffer so
// MSD and VDOS recording can be toggled independently.
export const POSITION_BUFFER_CAPACITY = 25000;
export const POSITION_BUFFER_MAX_ATOMS = 16;

// Rolling series for chat-driven trajectory-SEGMENT analysis (see
// src/agent/trajectorySegment.ts). Sampled every collectInterval — the same
// always-on cadence as the energy series — so a recent time window is always
// available to slice WITHOUT the user starting any recording. At the default
// 100-step interval / 0.5 fs that's a sample every ~50 fs; 4000 samples ≈ 200 ps
// of look-back. Speed frames are ~120 floats (≈ 0.5 KB) each → < 2 MB total;
// the temperature series is negligible.
export const TRAJECTORY_SERIES_CAPACITY = 4000;

// One energy sample of the long recording behind the chat's energy card: the
// Energy tab keeps only its last 100 samples (energyTimeSeries), so a card asked
// for the whole run reads this instead. Energies per atom, eV.
export interface EnergyHistorySample {
  time: number; // fs
  k: number;
  p: number;
  t: number;
}

// The Protein / DNA-RNA region MD's energies for the chat card (the Analyze panel
// keeps its own short ring). kcal/mol over the region; t in fs.
export interface BioRegionSample {
  t: number;
  ke: number;
  pe: number;
  temperatureK: number;
}

export interface TemperatureSample {
  tFs: number;
  tempK: number;
  // The thermostat setpoint at that moment (null with the thermostat off), so a
  // card can tell a deliberate temperature step from a small system's jitter.
  // Absent in the crystal lab and in older recordings.
  setpointK?: number | null;
}

export interface DataStoreState {
  energyTimeSeries: DataQueue<EnergyData>;
  // Whole-system molecular-species census sampled per collectInterval while a
  // reactive run is playing. Feeds the reaction population-vs-time plot — each
  // sample is a {formula: count} snapshot. Empty outside reactive mode.
  speciesTimeSeries: DataQueue<SpeciesPopulationData>;
  positionTimeSeriesMap: Map<number, DataQueue<PositionData>>;
  speedArrayMap: Map<number, DataQueue<number>>;
  // Rolling buffer of dipole-current samples J(t) = Σᵢ qᵢ vᵢ(t) used by
  // the IR-spectrum panel. Empty by default; populated when the user
  // enables recording via usePrimitiveStore.recordDipoleCurrent.
  dipoleCurrentBuffer: DipoleCurrentBuffer;
  // Per-atom velocity ring buffer feeding the phonon-DOS panel. The
  // tracked-atom set is locked in when the user first starts recording
  // (panel calls .prepare(atoms) once); empty otherwise. Separate from
  // the dipole buffer because DOS averages per-atom VACFs rather than
  // autocorrelating a single velocity sum — see vibrationalDos.ts.
  velocityBuffer: VelocityBuffer;
  // Per-atom position ring buffer feeding the MSD / diffusion panel.
  // Same shape as the velocity buffer; PBC-unwrap is handled inside
  // PositionBuffer.push when a container is supplied.
  positionBuffer: PositionBuffer;
  // Time series of the picked geometric measurements — one row per
  // collectInterval holding {time, values: id→value} across every track in
  // projectState.measurementTracks, sampled while recordMeasurement is on. Feeds
  // the "Measurement" analysis tab. Ephemeral (regenerated by recording) even
  // though the track definitions themselves are persisted with the project.
  measurementTimeSeries: DataQueue<MeasurementRow>;
  // (temperature, plate-height / volume / density) samples for the piston
  // barostat's boiling-point readout. Ephemeral; cleared on reset.
  pistonTimeSeries: DataQueue<PistonRow>;
  // Always-on rolling series for trajectory-segment analysis: a time-stamped
  // all-atom speed histogram per collect, and the instantaneous temperature per
  // collect. Populated unconditionally in the sim loop's collect(); sliced by
  // time window in the analyzeTrajectorySegment agent tool.
  speedHistogramSeries: DataQueue<SpeedHistogramFrame>;
  temperatureSeries: DataQueue<TemperatureSample>;
  energyHistorySeries: DataQueue<EnergyHistorySample>;
  bioRegionHistorySeries: DataQueue<BioRegionSample>;
  // A polymer chain's size over its whole backbone, for the chat card.
  chainSizeSeries: DataQueue<{ time: number; rg: number; ree: number }>;
  // The Reaction tab keeps its last 100 samples; the chat card reads this one.
  speciesHistorySeries: DataQueue<SpeciesPopulationData>;
  // Empty EVERY per-run series above (the engine clock is restarting, so any
  // retained sample would sit under a later timestamp than the new ones and
  // corrupt Δt-based analyses — the IR wavenumber axis first of all). The one
  // place to extend when a new per-run buffer is added; called from the sim
  // controls' reset and from the viewer's clearModel (molecule set changed).
  // positionTimeSeriesMap entries are emptied, not removed — callers that drop
  // the atoms they belong to clear the map itself afterwards.
  clearRunSeries: () => void;
}

export const useDataStore = createWithEqualityFn<DataStoreState>()((set, get) => {
  return {
    clearRunSeries: () => {
      const s = get();
      s.energyTimeSeries.clear();
      s.speciesTimeSeries.clear();
      s.measurementTimeSeries.clear();
      s.pistonTimeSeries.clear();
      s.speedArrayMap.clear();
      s.speedHistogramSeries.clear();
      s.temperatureSeries.clear();
      s.energyHistorySeries.clear();
      s.bioRegionHistorySeries.clear();
      s.chainSizeSeries.clear();
      s.speciesHistorySeries.clear();
      s.dipoleCurrentBuffer.clear();
      s.velocityBuffer.clear();
      s.positionBuffer.clear();
      s.positionTimeSeriesMap.forEach((series) => series.clear());
    },
    energyTimeSeries: new DataQueue<EnergyData>(DATA_QUEUE_LENGTH),
    speciesTimeSeries: new DataQueue<SpeciesPopulationData>(DATA_QUEUE_LENGTH),
    positionTimeSeriesMap: new Map<number, DataQueue<PositionData>>(),
    speedArrayMap: new Map<number, DataQueue<number>>(),
    dipoleCurrentBuffer: new DipoleCurrentBuffer(DIPOLE_BUFFER_CAPACITY),
    velocityBuffer: new VelocityBuffer(VELOCITY_BUFFER_CAPACITY, VELOCITY_BUFFER_MAX_ATOMS),
    positionBuffer: new PositionBuffer(POSITION_BUFFER_CAPACITY, POSITION_BUFFER_MAX_ATOMS),
    measurementTimeSeries: new DataQueue<MeasurementRow>(MEASUREMENT_BUFFER_CAPACITY),
    pistonTimeSeries: new DataQueue<PistonRow>(MEASUREMENT_BUFFER_CAPACITY),
    speedHistogramSeries: new DataQueue<SpeedHistogramFrame>(TRAJECTORY_SERIES_CAPACITY),
    temperatureSeries: new DataQueue<TemperatureSample>(TRAJECTORY_SERIES_CAPACITY),
    energyHistorySeries: new DataQueue<EnergyHistorySample>(TRAJECTORY_SERIES_CAPACITY),
    bioRegionHistorySeries: new DataQueue<BioRegionSample>(TRAJECTORY_SERIES_CAPACITY),
    chainSizeSeries: new DataQueue<{ time: number; rg: number; ree: number }>(TRAJECTORY_SERIES_CAPACITY),
    speciesHistorySeries: new DataQueue<SpeciesPopulationData>(TRAJECTORY_SERIES_CAPACITY),
  };
});
